import OrgNodeCard from './OrgNodeCard';
import './orgChart.css';

/**
 * Indented list. Same forest and the same card as the tree, so the two views
 * cannot drift. Better than the tree at depth and on narrow screens.
 */
function ListNode({ node, collapsed, onToggle, matches, focusId, isAdmin }) {
  const hasKids = node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const dim = !matches(node);

  // The whole row toggles, not just the chevron: a row is a big target and
  // clicking it is what a reader expects from a collapsible list. The card
  // contains a profile link, so a click that originated inside an anchor or a
  // button is left alone, otherwise opening someone's profile would also collapse
  // their team on the way out.
  const onRowClick = (e) => {
    if (!hasKids) return;
    if (e.target.closest('a, button')) return;
    onToggle(node.id);
  };

  return (
    <div className="oc-list-node">
      {/* Not a <button>: the row contains a link, and a button cannot legally
          contain interactive content. Given the explicit chevron button already
          carries the accessible name and aria-expanded, this outer handler is a
          mouse convenience, so it takes role/tabIndex only when it does something. */}
      <div
        className={[
          'oc-list-row',
          focusId === node.id ? 'oc-focus' : '',
          hasKids ? 'oc-list-row--clickable' : '',
        ].filter(Boolean).join(' ')}
        style={{ opacity: dim ? 0.62 : 1 }}
        onClick={onRowClick}
      >
        {hasKids ? (
          <button
            type="button"
            className="oc-chev"
            onClick={() => onToggle(node.id)}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? `Expand ${node.name}` : `Collapse ${node.name}`}
          >
            {/* One rotating chevron rather than two swapped glyphs: the rotation
                animates the state change, and there is a single shape to style. */}
            <svg
              className={`oc-chev-icon${isCollapsed ? '' : ' oc-chev-icon--open'}`}
              viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false"
            >
              <path d="M6 4l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {/* Direct-report count, matching the tree's collapse toggle. Same
                `children.length` the tree uses, so the two views can never report
                a different number for the same node. Aria-hidden because the
                button's own aria-label already names the action, and a bare digit
                announced after "Expand Sandra" reads as noise. */}
            <span className="oc-chev-count" aria-hidden="true">{node.children.length}</span>
          </button>
        ) : (
          <span className="oc-chev-spacer" aria-hidden="true" />
        )}
        <OrgNodeCard node={node} variant="list" isAdmin={isAdmin} />
      </div>
      {hasKids && !isCollapsed && (
        <div className="oc-list-children">
          {node.children.map((c) => (
            <ListNode
              key={c.id} node={c} collapsed={collapsed} onToggle={onToggle}
              matches={matches} focusId={focusId} isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function OrgListView({
  roots, collapsed, onToggle, matches, focusId, isAdmin, viewportRef, fullscreenOverlay = null,
}) {
  return (
    <div className="oc-viewport oc-viewport--list" ref={viewportRef}>
      {roots.map((r) => (
        <ListNode
          key={r.id} node={r} collapsed={collapsed} onToggle={onToggle}
          matches={matches} focusId={focusId} isAdmin={isAdmin}
        />
      ))}
      {fullscreenOverlay}
    </div>
  );
}
